import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Zap, Activity, BarChart3, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { getConverterApiBase } from "../api";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";

type SharedMcp = {
  token: string;
  label: string | null;
  mcp: {
    server_id: string;
    title: string;
    tool_count: number;
    created_at: string;
  };
  stats: {
    calls_7d: number;
    calls_today: number;
    total_calls: number;
    top_tools: { tool: string; count: number }[];
    clients: { client: string; calls: number }[];
  };
};

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl bg-muted/50 border border-border p-4 text-center">
      <p className="text-2xl font-bold tabular-nums text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

export default function SharePage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharedMcp | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${getConverterApiBase()}/api/share/${token}`)
      .then(r => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then(d => { if (d) setData(d as SharedMcp); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const mcpUrl = data
    ? `${getConverterApiBase()}/mcp/${data.mcp.server_id}`
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <TopHeader />
      <main className="flex-1">
        <div className="max-w-xl mx-auto px-4 py-12">
          {loading && (
            <p className="text-center text-muted-foreground text-sm">Loading…</p>
          )}

          {notFound && (
            <div className="text-center space-y-3">
              <p className="text-xl font-bold text-foreground">Link not found</p>
              <p className="text-sm text-muted-foreground">This share link may have expired or been revoked.</p>
              <Link to="/" className="text-sm text-primary underline">Go home</Link>
            </div>
          )}

          {data && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                  <Activity size={22} className="text-blue-500" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-foreground">{data.mcp.title}</h1>
                  {data.label && <p className="text-sm text-muted-foreground mt-0.5">{data.label}</p>}
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                    <Zap size={11} className="text-blue-400" />
                    {data.mcp.tool_count} tools
                  </p>
                </div>
              </div>

              {/* MCP URL */}
              {mcpUrl && (
                <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-xl px-4 py-3">
                  <code className="flex-1 text-xs font-mono text-foreground break-all">{mcpUrl}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(mcpUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="shrink-0 p-1.5 rounded-lg hover:bg-background text-muted-foreground transition-colors"
                  >
                    {copied ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Copy size={14} />}
                  </button>
                  <a href={mcpUrl} target="_blank" rel="noreferrer" className="shrink-0 p-1.5 rounded-lg hover:bg-background text-muted-foreground transition-colors">
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Calls today" value={data.stats.calls_today.toLocaleString()} />
                <StatCard label="Calls (7 days)" value={data.stats.calls_7d.toLocaleString()} />
                <StatCard label="Total calls" value={data.stats.total_calls.toLocaleString()} />
              </div>

              {/* Top tools */}
              {data.stats.top_tools.length > 0 && (
                <div className="rounded-2xl border border-border bg-card overflow-hidden">
                  <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                    <BarChart3 size={13} className="text-blue-500" />
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Top tools</p>
                  </div>
                  <div className="divide-y divide-border">
                    {data.stats.top_tools.map(t => (
                      <div key={t.tool} className="flex items-center justify-between px-5 py-3">
                        <code className="text-xs font-mono text-foreground">{t.tool}</code>
                        <span className="text-xs font-semibold tabular-nums text-muted-foreground">{t.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Clients */}
              {data.stats.clients.length > 0 && (
                <div className="rounded-2xl border border-border bg-card overflow-hidden">
                  <div className="px-5 py-3 border-b border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">AI clients</p>
                  </div>
                  <div className="divide-y divide-border">
                    {data.stats.clients.map(c => (
                      <div key={c.client} className="flex items-center justify-between px-5 py-3">
                        <span className="text-sm text-foreground">{c.client}</span>
                        <span className="text-xs font-semibold tabular-nums text-muted-foreground">{c.calls.toLocaleString()} calls</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-center text-xs text-muted-foreground">
                Powered by <Link to="/" className="underline">API to MCP Converter</Link>
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
